import { Alert, AlertTitle, Box, Button, CircularProgress, Divider, Paper, Snackbar, Stack, Typography } from '@mui/material';
import { Redirect } from 'react-router-dom';
import PipelineGraph from './PipelineGraph/PipelineGraph';
import BottomNavigationBar from './BottomNavigationBar/BottomNavigationBar';
import ServiceDrawer from './ServiceDrawer/ServiceDrawer';
import ControlNavigationBar from './ControlNavigationBar/ControlNavigationBar';
import useGetPipelinesByEmail from '../../shared/hooks/useGetPipelinesByEmail';
import { Component, MutableRefObject, useEffect, useRef, useState } from 'react';
import { useSavePipelineMutation } from '../../store/data/dataSlice';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import LoadingOverlay from 'react-loading-overlay-ts';
import { Pipeline as PipelineType } from '../../store/data/types';
import { Ged } from '../../store/processing/types';
import { useDeletePipelineMutation, useRunPipelineMutation, useGetPipelinesQuery, useEvalPipelineMutation } from '../../store/processing/processingSlice';
import styles from './pipeline.module.css';
import clone from 'just-clone';
import { IEdge, IGraphViewProps, INode, SelectionT, Node } from 'react-digraph';
import PipelineReducer from '../../util/pipelineReduceUtil';
import { Service } from '../../store/service/types';
import AddServiceDialog from './AddServiceDialog/AddServiceDialog';
import EditServiceDialog from './EditServiceDialog/EditServiceDialog';
import AddEdgeDialog from './BottomNavigationBar/AddEdgeDialog';
import EditEdgeDialog from './BottomNavigationBar/EditEdgeDialog';
import AddServiceDialogBottom from './BottomNavigationBar/AddServiceDialogBottom';
import { SelectedValueType } from './AddServiceDialog/parameter/parameterSelector';
import { v4 as uuidv4 } from 'uuid';
import { isFulfilled } from '@reduxjs/toolkit';
import { useGetPipelineRunStateQuery } from '../../store/processing/processingSlice';
import { useDispatch } from 'react-redux';
import pipelineSlice, { setPid } from '../../store/pipeline/pipelineSlice';
import { clearPipeline, setError, setIsRunning, setNewResult } from '../../store/pipeline/pipelineSlice';
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import ErrorList, { PipelineError } from './ErrorList/ErrorList';
import { NODE_KEY } from './PipelineGraph/graphConfig';
import { insertNewResult } from '../../store/results/resultsSlice';
import i18n from '../../locales/i18n';
import { togglePauseTut } from '../../store/tutorial/tutorialSlice';


type Props = {
  pid: string;
};

type ErrorData = {
  detail: PipelineError[];
}

/**
 * Important: the pipeline is not immutable because of ract-digraph
 * The pipeline in the global redux state is not mutated.
 * The pipeline is copied to the local state and then mutated.
 * This component also saves selected nodes/edges and actions of the
 * BottomNavigationBar.
 */
const Pipeline = ({ pid }: Props): JSX.Element => {
  const email = useAppSelector(store => store.usertoken.token);
  const { data, error, isLoading } = useGetPipelinesByEmail();
  const dispatch = useAppDispatch();
  const pipeLineState = useAppSelector(state => state.pipeline);
  const [notify, setNotify] = useState(false as boolean);
  const [undoDisabled, setUndoDisabled] = useState(true as boolean);
  const [errorOpen, setErrorOpen] = useState(false as boolean);
  const [errorDetail, setErrorDetail] = useState([{error: "", error_code: 0, description: ""}] as PipelineError[]);
  const [pipelineStack, setPipelineStack] = useState([] as PipelineType[]);
  const [pipelineSaved, setPipelineSaved] = useState(false as boolean);
  const tutorial = useAppSelector(state => state.tutorial);


  const [savePipeline, { error: saveError, isLoading: saveIsLoading }] = useSavePipelineMutation();
  const [runPipeline] = useRunPipelineMutation();
  const [deletePipeline] = useDeletePipelineMutation();
  const [evalPipeline] = useEvalPipelineMutation();

  const [pipeline, setPipeline] = useState(null as PipelineType | null | undefined);
  const [selected, setSelected] = useState({nodes: null, edges: null} as SelectionT);

  const [clicked, setClicked] = useState(false as boolean);
  const [clickedService, setClickedService] = useState(false as boolean);
  const [edgeSelected, setEdgeSelected] = useState(false as boolean);

  const [addServiceType, setAddServiceType] = useState(null as Service | null);
  const [editServiceType, setEditServiceType] = useState(null as Service | null);
  const [addServiceTypeBottomBar, setAddServiceTypeBottomBar] = useState(null as Service | null);

  const graphRef = useRef<Component<IGraphViewProps, unknown, unknown>>() as MutableRefObject<
    Component<IGraphViewProps, unknown, unknown> & {
      panToNode: (id: string, zoom?: boolean) => void;
      panToEdge: (source: string, target: string, zoom?: boolean) => void;
    }
  >;

  useEffect(() => {
    setSelected({nodes: null, edges: null});
  }, [pid]);

  useEffect(() => {
    const interval = setInterval(() => {
      setPipelineSaved(true);
      onSave();
    }, 300000)
    return () => clearInterval(interval);
  }, []);

  //Trigger pausing the tutorial at specific steps to use custom triggers to unpause
  useEffect(() => {
    if(tutorial.active && !tutorial.paused && tutorial.actionSteps.includes(tutorial.stepIndex) && pipeline !== null && pipeline !== undefined) {
      console.log(pipeline);
      dispatch(togglePauseTut());
    }
  }, [pipeline])

  useEffect(() => {
    if (error) {
      setPipeline(null);
    } else {
      const filtered = data?.filter(pipeline => pipeline.pid === pid);
      if (error || !filtered || filtered.length !== 1) {
        setPipeline(undefined);
      } else {
        setPipeline(clone(filtered[0]));
      }
    }
  }, [data, error, pid]);

  // "temporary fix": could be replaced by another graph representation
  useEffect(() => {
    dispatch(clearPipeline());
    if (pipeline) {
      pipeline.graph.nodes.forEach(node => {
        const service = pipeline.pipeline.services[node.id];
        if (service) {
          node.type = service.service;
        }
      });
      pipelineStack.push(pipeline);
      setUndoDisabled((pipelineStack.length < 2))
    }
  }, [pipeline]);

  if (pipeline === undefined) {
    return <Redirect to={'/'} />;
  }

  const onUndo = () => {
    //Need to pop one additional time here, since updating the pipeline immediately pushes newly selected pipeline onto the stack
    //Therefore the highest pipeline is always the current one, which needs to be popped off first to get to the last one
    pipelineStack.pop()
    setPipeline(pipelineStack.pop())
  }

  const onSave = () => {
    if (!email || !pipeline) {
      //TODO: handle error
      return;
    }
    if(tutorial.active) {
      if(!tutorial.paused && tutorial.actionSteps.includes(tutorial.stepIndex)) dispatch(togglePauseTut());
    }

    const newPipeline = { ...pipeline, user: email };
    savePipeline({ pid: pipeline.pid, pipeline: newPipeline });
    setPipelineSaved(true);
  };

  const onServiceDrawerServiceClick = (service: Service):void => {
    if(tutorial.actionSteps.includes(tutorial.stepIndex)) dispatch(togglePauseTut());
    setAddServiceType(service);
  }

  async function onRun() {
    if(tutorial.active) {
      if(!tutorial.paused && tutorial.actionSteps.includes(tutorial.stepIndex)) dispatch(togglePauseTut());
    }
    dispatch(clearPipeline());
    if (pipeline && pipeline.pid) {
      dispatch(setPid(pipeline.pid));
      const runPipelineResponse = await runPipeline({ pid: pipeline.pid, pipeline: pipeline });
      if ('error' in runPipelineResponse && 'data' in runPipelineResponse.error) {
        const detail = (runPipelineResponse.error.data as ErrorData).detail
        dispatch(setError(true));
        setNotify(true);
        setErrorDetail(detail);
      }
      else {
        dispatch(setIsRunning(true));
      }
    }
    else {
      alert('no active pipeline!');
    }
  }

  async function onEval() {
    const ged = await evalPipeline({ pid: pipeline?.pid, ref_pid: "51342799-8171-4a3a-9046-3e751321ca37" });
    if ('data' in ged) {
      const actual_ged = ged.data as Ged;
      alert(actual_ged.ged);
    }
  }

  const onStop = () => {
    dispatch(setIsRunning(false));
    deletePipeline(pid);
    dispatch(setNewResult(true));
    setNotify(true);
    setTimeout(()=>{
      if(tutorial.active) {
        if(!tutorial.paused && tutorial.actionSteps.includes(tutorial.stepIndex)) dispatch(togglePauseTut());
      }
    }, 1000);
  };

  const onCenterEdge = (): void => {
    const edges = selected?.edges;
    if (edges && edges.size === 1) {
      const source = edges.values().next().value.source;
      const target = edges.values().next().value.target;
      graphRef.current.panToEdge(source, target, false);
    }
  };

  const handleNotificationClose = (): void => {
    setNotify(false);
  }

  const updateEdgeLabeling = (edges: IEdge[]): void => {
    if(!edges) return;
    edges.forEach((edge) => {
      if(edge.handleText && pipeline) {
        const deleted_label = Number(edge.handleText);
        pipeline.graph.edges.filter(filterEdge => (filterEdge.target === edge.target) && (Number(filterEdge.handleText) > deleted_label)).forEach((filterEdge) => {
          filterEdge.handleText = String(Number(filterEdge.handleText) - 1);
        });
      }
    });
  };

  const onDeleteSelected = (selected: SelectionT): void => {
    if(!pipeline) return;

    if(tutorial.active) {
      if(!tutorial.paused && tutorial.actionSteps.includes(tutorial.stepIndex)) dispatch(togglePauseTut());
    }

    
    const nodes = selected.nodes;
    let newNodes:INode[] = pipeline.graph.nodes;
    let newEdges:IEdge[] = pipeline.graph.edges;
    let edges_to_be_deleted:IEdge[] = [];

    if(selected.edges) {
      edges_to_be_deleted = Array.from(selected.edges.values());
    }

    if (nodes && nodes.size > 0 && pipeline) {
      nodes.forEach(node => {
        delete pipeline.pipeline.services[node.id]
      })
      newNodes = pipeline.graph.nodes.filter(node => !nodes.has(node.id));
      pipeline.graph.edges.filter(
        edge => nodes.has(edge.source) || nodes.has(edge.target)
      ).forEach(wrong_edge => {
        edges_to_be_deleted.push(wrong_edge);
      });
    }
    
    if(edges_to_be_deleted.length > 0) {
      updateEdgeLabeling(edges_to_be_deleted);
      newEdges = pipeline.graph.edges.filter(edge => !edges_to_be_deleted.includes(edge));
    }

    setPipeline(PipelineReducer.setGraph(pipeline, {nodes: newNodes, edges: newEdges}))
    setSelected({nodes: null, edges: null});
  };

  const onEditSelectedEdge = (success: boolean, name: string, serviceFrom: string, serviceTo: string): void => {
  // implemented via deleting selected edge and pushing updated one to graph.edges
    const selectedEdge = selected?.edges?.values().next().value;
    if (selectedEdge && pipeline) {
      if (success) {
        const edge = {
          source: serviceFrom, //IDs of sorce and target services
          target: serviceTo,
          handleText: String(pipeline.graph.edges.filter(edge => edge.target ==serviceTo).length + 1),
          type: 'emptyEdge',
        };

        updateEdgeLabeling([selectedEdge])
        const edges = (pipeline.graph.edges.filter(edge => edge !== selectedEdge)).concat(edge);
        setPipeline(PipelineReducer.setEdges(pipeline, edges));
      }
      setEdgeSelected(false);
    }
  };

  const onCenter = (): void => {
    if(selected?.nodes && selected.nodes.size > 0) {
      const id = selected.nodes.keys().next().value;
      graphRef.current.panToNode(id, false);
    }
    else if (selected?.edges && selected.edges.size > 0) {
      const source = selected.edges.values().next().value.source;
      const target = selected.edges.values().next().value.target;
      graphRef.current.panToEdge(source, target, false);
    }

    const nodes = selected?.nodes;
    if (nodes && nodes.size === 1) {
      const id = nodes.keys().next().value;
      graphRef.current.panToNode(id, false);
    }
  };

  const onEditSelectedService = (success: boolean, name: string, data: Record<string, SelectedValueType>): void => {
    const nodes = selected?.nodes;
    if (nodes && nodes.size === 1 && pipeline) {
      const id = nodes.keys().next().value;
      const service = pipeline.pipeline.services[id]; //service from data/types
      if (!pipeline || !editServiceType) {
        // TODO: handle error 
        return;
      }
      if (success) {
        const parameters: Record<string, string> = {};
        Object.entries(data).forEach(([key, value]: [string, SelectedValueType]) => (parameters[key] = value.toString()));

        service.module = editServiceType.module;
        service.service = editServiceType.service;
        service.version = editServiceType.version;
        service.username = name;
        service.parameters = parameters;
        
        nodes.values().next().value.title = name;
        nodes.values().next().value.type = editServiceType.service;
        if(i18n.language === 'de') {
          nodes.values().next().value.typeText = editServiceType.name_de;
        }
        else {
          nodes.values().next().value.typeText = editServiceType.name_en;
        }
        const newPipeline = clone(pipeline);
        newPipeline.pipeline.services[id] = service;

        setPipeline(newPipeline);
      }
      setEditServiceType(null);
    }
  };

  const onCreateEdge = (sourceNode: INode, targetNode: INode): void => {
      if(!pipeline) {
        return;
      }
      if(tutorial.active) {
        if(!tutorial.paused && tutorial.actionSteps.includes(tutorial.stepIndex)) dispatch(togglePauseTut());
      }
      const newPipeline = clone(pipeline);
      const edge = {
        source: sourceNode[NODE_KEY], //IDs of sorce and target services
        target: targetNode[NODE_KEY],
        handleText: String(newPipeline.graph.edges.filter(edge => edge.target == targetNode[NODE_KEY]).length + 1),
        type: 'emptyEdge'
      }
      newPipeline.graph.edges.push(edge);
      setPipeline(newPipeline);
  };

  const onSwapEdge = (sourceNode: INode, targetNode: INode, viewEdge: IEdge): void => {
    if(!pipeline) return;

    const newEdge = {
      source: sourceNode[NODE_KEY], //IDs of sorce and target services
      target: targetNode[NODE_KEY],
      handleText: String(pipeline.graph.edges.filter(edge => edge.target == targetNode[NODE_KEY]).length + 1),
      type: 'emptyEdge'
    }
    updateEdgeLabeling([viewEdge]);
    const edges = (pipeline.graph.edges.filter(edge => edge !== viewEdge)).concat(newEdge);

    setPipeline(PipelineReducer.setEdges(pipeline, edges));
  };
 
  const onAddService = (success: boolean, name: string, serviceType: Service | null, data: Record<string, SelectedValueType>): void => {
    setAddServiceTypeBottomBar(serviceType);
    if (!pipeline || !addServiceTypeBottomBar) {
      // TODO: handle error (if pipeline is empty, no services will be created)
      setClickedService(false);
      return;
    }
    if (success) {
      const newPipeline = clone(pipeline);
      const serviceUuid = uuidv4();
      const parameters: Record<string, string> = {};
      Object.entries(data).forEach(([key, value]: [string, SelectedValueType]) => (parameters[key] = value.toString()));
      const service = {
        module: addServiceTypeBottomBar.module,
        service: addServiceTypeBottomBar.service,
        version: addServiceTypeBottomBar.version,
        username: name,
        parameters,
      };

      const node = {
        id: serviceUuid,
        title: name,
        type: addServiceTypeBottomBar.type,
        //type: addServiceTypeBottomBar.service, 
        typeText: i18n.language === 'de' ? addServiceTypeBottomBar.name_de : addServiceTypeBottomBar.name_en,
        x: 0,
        y: 0,
      };
      newPipeline.graph.nodes.push(node);
      newPipeline.pipeline.services[serviceUuid] = service;
      setPipeline(newPipeline);
    }
    setAddServiceTypeBottomBar(null);
    setClickedService(false); // to have window with ServiceDrawer opened after adding 1 service, delete this line
  };
  
  const onAddEdge = (success: boolean, serviceFrom: string, serviceTo: string[]): void => {
    if (!pipeline) {
      // TODO: handle error (if pipeline is empty, no edge will be added)
      return;
    }
    if (success) {
      if(tutorial.active) {
        if(!tutorial.paused && tutorial.actionSteps.includes(tutorial.stepIndex)) dispatch(togglePauseTut());
      }

      const newPipeline = clone(pipeline);
      serviceTo.forEach(value => {
        const edge = {
          source: serviceFrom, //IDs of sorce and target services
          target: value,
          handleText: String(newPipeline.graph.edges.filter(edge => edge.target == value).length + 1),
          type: 'emptyEdge',
        };
        newPipeline.graph.edges.push(edge);
      });
      setPipeline(newPipeline);
    } 
    setClicked(false);
  };

  const onAddSpecificService = (success: boolean, name: string, data: Record<string, SelectedValueType>): void => {
    if (!pipeline || !addServiceType) {
      // TODO: handle error (if pipeline is empty, no services will be added)
      return;
    }
    if (success) {      
      const newPipeline = clone(pipeline);
      const serviceUuid = uuidv4();
      const parameters: Record<string, string> = {};
      Object.entries(data).forEach(([key, value]: [string, SelectedValueType]) => (parameters[key] = value.toString()));
      const service = {
        module: addServiceType.module,
        service: addServiceType.service,
        version: addServiceType.version,
        username: name,
        parameters,
      };
      const node = {
        id: serviceUuid,
        title: name,
        type: addServiceType.type, 
        typeText: i18n.language === 'de' ? addServiceType.name_de : addServiceType.name_en ,
        x: 0,
        y: 0,
      };
      newPipeline.graph.nodes.push(node);
      newPipeline.pipeline.services[serviceUuid] = service;
      setPipeline(newPipeline);
    }
    setAddServiceType(null);
  };

  return (
    <div className='PipelineEditor-paper'>
      <Paper elevation={2} sx={{ px: 2, py: 2 }}>
        <Stack direction={'column'} spacing={1}>
          <Stack direction={'row'} justifyContent={'space-between'}>
            {!pipeline ? <CircularProgress /> : <Typography variant={'h4'}>{pipeline.pipeline.name}</Typography>}
            <ControlNavigationBar onUndo={onUndo} undoDisabled={undoDisabled} onSave={onSave} onRun={onRun} onStop={onStop} saveError={saveError} saveIsLoading={saveIsLoading} pid={pid} />
          </Stack>
          <Divider />
          <Stack direction={'row'}>
            <Box style={{ flexBasis: '75%' }}>
              <LoadingOverlay active={isLoading || saveIsLoading} spinner className={styles.fullHeight}>
                {pipeline ? (
                  <PipelineGraph pipeline={pipeline} selected={selected} onDeleteSelected={onDeleteSelected} onSelect={setSelected} onCreateEdge={onCreateEdge} onSwapEdge={onSwapEdge} onUndo={onUndo} ref={graphRef} />
                ) : (
                  <CircularProgress />
                )}
              </LoadingOverlay>
            </Box>
            <Box style={{ flexBasis: '25%' }}>
              <ServiceDrawer onClick={service => onServiceDrawerServiceClick(service)} />
              <AddServiceDialog service={addServiceType} graphInfo={pipeline? pipeline.graph : null } onClose={onAddSpecificService} />
            </Box>
          </Stack>
          <Divider />
          <BottomNavigationBar
            onCenterEdge={onCenterEdge}
            onAddEdge={clicked => setClicked(clicked)}
            onEditEdge={edgeSelected => setEdgeSelected(edgeSelected)}
            selected={selected}

            onDelete={onDeleteSelected}
            onCenter={onCenter}
            onEditService={service => setEditServiceType(service)}
            onAddService={clickedService => setClickedService(clickedService)}
            onEval={onEval}

            nodesSelected={selected?.nodes?.size ?? 0}
            edgesSelected={selected?.edges?.size ?? 0}
            typeSelected={selected?.nodes?.size===1? selected?.nodes?.values().next().value.type : null }
          />
          {pipeLineState.error ? (
            <ErrorList detail={errorDetail}></ErrorList>
          ) : <></>
          }
          <AddServiceDialogBottom open={clickedService} pipeline={pipeline? pipeline : null} onClose={onAddService} />
          <EditServiceDialog service={editServiceType} selected={selected}  onClose={onEditSelectedService} serviceValues={
            pipeline?.pipeline.services[selected?.nodes?.keys().next().value]}/>
          <EditEdgeDialog open={edgeSelected} graphInfo={pipeline? pipeline.graph : null } selected={selected} onClose={onEditSelectedEdge}/>
          <AddEdgeDialog open={clicked} graphInfo={pipeline? pipeline.graph : null } onClose={onAddEdge} />
          <Snackbar open={notify} anchorOrigin={{vertical: 'bottom', horizontal: 'right'}} autoHideDuration={10000} onClose={handleNotificationClose}>
              {
                pipeLineState.error === true ? 
                  <Alert onClose={handleNotificationClose} severity={'error'} sx={{ width: '100%'}} variant="filled">
                    <AlertTitle>Error</AlertTitle>
                    Your pipeline is <strong>invalid!</strong>
                  </Alert>
                  :
                  <Alert onClose={handleNotificationClose} severity={'success'} sx={{ width: '100%'}} variant="filled">
                    <AlertTitle>Success</AlertTitle>
                    Your pipeline succeeded and produced a result!
                  </Alert>
              }
          </Snackbar>
          <Snackbar open={pipelineSaved} anchorOrigin={{vertical: 'top', horizontal: 'right'}} autoHideDuration={3000} onClose={()=> {setPipelineSaved(false)}}>
            <Alert severity={'success'} sx={{ width: '100%'}} variant="filled" onClose={()=> {setPipelineSaved(false)}}>
              Your pipeline has been saved.
            </Alert>
          </Snackbar>
        </Stack>
      </Paper>
    </div>
  );
};

export default Pipeline;
